'use client';

import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-normal transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-background hover:bg-accent-dark',
        secondary:
          'border border-foreground text-foreground hover:bg-foreground hover:text-background',
        ghost: 'text-foreground underline underline-offset-4 hover:text-accent',
        link: 'text-foreground underline decoration-solid uppercase tracking-tight hover:text-accent',
      },
      size: {
        sm: 'h-9 px-4 text-sm rounded-md',
        md: 'h-11 px-6 text-body rounded-lg',
        lg: 'h-14 px-8 text-body-lg rounded-lg',
        icon: 'size-11 rounded-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Optional href to render as anchor */
  href?: string;
}

/**
 * Primary button component with multiple variants and sizes.
 * Renders as `<a>` when href is provided, `<button>` otherwise.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, href, children, ...props }, ref) => {
    if (href) {
      return (
        <a href={href} className={cn(buttonVariants({ variant, size, className }))}>
          {children}
        </a>
      );
    }

    return (
      <button ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props}>
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';

export { buttonVariants };
